import * as THREE from "three";
import { scene } from "./core";
import { VISUAL_CONFIG } from "@shared/config";
import type { IMazeCell } from "@shared/types";
import { getRocketMeshes } from "./gameState";

// Create shared geometries and materials with higher detail for better visual quality
// This prevents creating new geometries and materials for every mesh
const sharedGeometries = {
  floor: new THREE.PlaneGeometry(1, 1),
  wall: new THREE.BoxGeometry(1, VISUAL_CONFIG.OBJECT.WALL_HEIGHT, 1),
  dot: new THREE.SphereGeometry(VISUAL_CONFIG.OBJECT.DOT_RADIUS, 16, 12), // Increased segments
};

const sharedMaterials = {
  floor: new THREE.MeshLambertMaterial({
    color: VISUAL_CONFIG.COLORS.FLOOR,
    side: THREE.DoubleSide,
    emissive: 0x000033,
    emissiveIntensity: 0.2,
  }),
  wall: new THREE.MeshLambertMaterial({
    color: VISUAL_CONFIG.COLORS.WALL,
    emissive: VISUAL_CONFIG.COLORS.WALL_EMISSIVE,
    emissiveIntensity: 0.6,
  }),
  dot: new THREE.MeshLambertMaterial({
    color: VISUAL_CONFIG.COLORS.DOT,
    emissive: VISUAL_CONFIG.COLORS.DOT_EMISSIVE,
    emissiveIntensity: 0.7,
  }),
  edgeLine: new THREE.LineBasicMaterial({
    color: 0x66ccff,
    transparent: true,
    opacity: 0.3,
  }),
};

// Optimize edge geometry creation
const wallEdgeGeometry = new THREE.EdgesGeometry(sharedGeometries.wall);

/**
 * Creates a maze cell in the 3D scene, including floor, walls, dots, and rockets.
 * Optimized for performance with larger mazes.
 */
export function createMazeCell(
  cell: IMazeCell,
  wallMeshes: Record<string, THREE.Mesh>,
  dotMeshes: THREE.Mesh[]
): void {
  // Use object distance from camera center to determine detail level
  const distanceFromCenter = Math.sqrt(
    (cell.x - Math.floor(cell.x / 2)) ** 2 +
      (cell.y - Math.floor(cell.y / 2)) ** 2
  );

  // Only create floor for cells within a reasonable distance
  // Far away floors don't add much visual detail
  if (distanceFromCenter < 40) {
    // Floor plane - reuse geometry and material
    const floor = new THREE.Mesh(sharedGeometries.floor, sharedMaterials.floor);
    floor.rotation.x = -Math.PI / 2;
    floor.position.set(
      cell.x + 0.5,
      VISUAL_CONFIG.OBJECT.FLOOR_Y,
      cell.y + 0.5
    );
    floor.receiveShadow = distanceFromCenter < 20; // Only enable shadows for nearby cells
    scene.add(floor);
  }

  // Create walls with optimized performance
  if (cell.walls[0]) {
    const wallKey = `${cell.x},${cell.y},0`;
    const mesh = createWall(
      cell.x + 0.5,
      cell.y,
      1,
      VISUAL_CONFIG.OBJECT.WALL_THICKNESS,
      distanceFromCenter
    );
    wallMeshes[wallKey] = mesh;
  }
  if (cell.walls[1]) {
    const wallKey = `${cell.x},${cell.y},1`;
    const mesh = createWall(
      cell.x + 1,
      cell.y + 0.5,
      VISUAL_CONFIG.OBJECT.WALL_THICKNESS,
      1,
      distanceFromCenter
    );
    wallMeshes[wallKey] = mesh;
  }
  if (cell.walls[2]) {
    const wallKey = `${cell.x},${cell.y},2`;
    const mesh = createWall(
      cell.x + 0.5,
      cell.y + 1,
      1,
      VISUAL_CONFIG.OBJECT.WALL_THICKNESS,
      distanceFromCenter
    );
    wallMeshes[wallKey] = mesh;
  }
  if (cell.walls[3]) {
    const wallKey = `${cell.x},${cell.y},3`;
    const mesh = createWall(
      cell.x,
      cell.y + 0.5,
      VISUAL_CONFIG.OBJECT.WALL_THICKNESS,
      1,
      distanceFromCenter
    );
    wallMeshes[wallKey] = mesh;
  }

  // Only create dots if the cell has them and is close enough to be visible
  if (cell.hasDot && distanceFromCenter < 30) {
    const dot = new THREE.Mesh(sharedGeometries.dot, sharedMaterials.dot);
    dot.position.set(
      cell.x + 0.5,
      VISUAL_CONFIG.OBJECT.DOT_RADIUS + 0.05,
      cell.y + 0.5
    );
    scene.add(dot);
    dotMeshes.push(dot);
  }

  // Rocket - use the createRocketMesh function directly
  if (cell.hasRocket) {
    // Import dynamically to avoid circular dependency
    import("./models/rocketModel").then(({ createRocketMesh }) => {
      const rocketGroup = createRocketMesh(cell.x + 0.5, cell.y + 0.5);
      // Store the rocket mesh for later removal when picked up
      getRocketMeshes().push(rocketGroup);
    });
  }
}

/**
 * Helper function to create a wall with performance optimizations.
 */
function createWall(
  x: number,
  z: number,
  width: number,
  depth: number,
  distanceFromCenter: number
): THREE.Mesh {
  // Scale the wall geometry
  const scaleMatrix = new THREE.Matrix4().makeScale(width, 1, depth);
  const scaledWallGeometry = sharedGeometries.wall
    .clone()
    .applyMatrix4(scaleMatrix);

  const wall = new THREE.Mesh(scaledWallGeometry, sharedMaterials.wall);
  wall.position.set(x, VISUAL_CONFIG.OBJECT.WALL_HEIGHT / 2, z);

  // Only enable shadows for walls close to the player
  if (distanceFromCenter < 20) {
    wall.castShadow = true;
    wall.receiveShadow = true;

    // Add edge lines only for closer walls for better performance
    if (distanceFromCenter < 15) {
      const edges = new THREE.LineSegments(
        wallEdgeGeometry,
        sharedMaterials.edgeLine
      );
      edges.scale.set(width, 1, depth);
      wall.add(edges);
    }
  }

  scene.add(wall);
  return wall;
}

/**
 * Destroy the specified wall in the 3D scene
 */
export function destroyWall(
  wallX: number,
  wallY: number,
  direction: number,
  wallMeshes: Record<string, THREE.Mesh>
): void {
  const wallKey = `${wallX},${wallY},${direction}`;
  const wall = wallMeshes[wallKey];
  if (wall) {
    createWallDestructionEffect(wall.position.clone());
    scene.remove(wall);
    delete wallMeshes[wallKey];
  } else {
    // If the wall mesh isn't found (e.g., it was already destroyed), still create the effect
    // at the approximate position of where the wall would be
    let x: number;
    let z: number;

    if (direction === 0) {
      // North
      x = wallX + 0.5;
      z = wallY;
    } else if (direction === 1) {
      // East
      x = wallX + 1;
      z = wallY + 0.5;
    } else if (direction === 2) {
      // South
      x = wallX + 0.5;
      z = wallY + 1;
    } else {
      // West
      x = wallX;
      z = wallY + 0.5;
    }

    createWallDestructionEffect(
      new THREE.Vector3(x, VISUAL_CONFIG.OBJECT.WALL_HEIGHT / 2, z)
    );
  }
}

/**
 * Basic effect for wall destruction
 */
export function createWallDestructionEffect(position: THREE.Vector3): void {
  const particleCount = 25; // Increased from 15 for more visual impact
  const explosionGroup = new THREE.Group();
  explosionGroup.userData = { id: `wallExplosion-${Date.now()}` };

  for (let i = 0; i < particleCount; i++) {
    let geometry: THREE.BufferGeometry;
    if (Math.random() > 0.7) {
      geometry = new THREE.BoxGeometry(
        0.05 + Math.random() * 0.1,
        0.05 + Math.random() * 0.1,
        0.05 + Math.random() * 0.1
      );
    } else {
      geometry = new THREE.TetrahedronGeometry(0.05 + Math.random() * 0.1);
    }

    const material = new THREE.MeshLambertMaterial({
      color: 0x0088ff,
      emissive: 0x0033aa,
    });

    const debris = new THREE.Mesh(geometry, material);
    debris.position.set(
      position.x + (Math.random() - 0.5) * 0.5,
      position.y + (Math.random() - 0.5) * 0.5,
      position.z + (Math.random() - 0.5) * 0.5
    );

    debris.userData = {
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 0.08, // Increased velocity for more dramatic effect
        Math.random() * 0.08 + 0.02,
        (Math.random() - 0.5) * 0.08
      ),
      age: 0,
      maxAge: 40 + Math.random() * 20, // Longer lasting particles
    };

    explosionGroup.add(debris);
  }

  // Add light flash
  const flashLight = new THREE.PointLight(0x66ccff, 2, 2);
  flashLight.position.copy(position);
  explosionGroup.add(flashLight);

  scene.add(explosionGroup);

  let isAnimating = true;
  function animateExplosion() {
    if (!isAnimating) return;

    let alive = false;
    for (const debris of explosionGroup.children) {
      if (debris.type === "PointLight") {
        // Fade out the light
        const light = debris as THREE.PointLight;
        light.intensity -= 0.2;
        if (light.intensity <= 0) {
          light.intensity = 0;
        } else {
          alive = true;
        }
        continue;
      }

      const ud = debris.userData;
      ud.age++;
      if (ud.age < ud.maxAge) {
        alive = true;
        debris.position.add(ud.velocity);
        ud.velocity.y -= 0.002; // Gravity effect

        // Add rotation to debris pieces
        debris.rotation.x += 0.05;
        debris.rotation.y += 0.05;

        // Scale down as they age
        if (ud.age > ud.maxAge * 0.7) {
          const scale = 1 - (ud.age - ud.maxAge * 0.7) / (ud.maxAge * 0.3);
          debris.scale.set(scale, scale, scale);
        }
      } else {
        debris.visible = false;
      }
    }
    if (alive) {
      requestAnimationFrame(animateExplosion);
    } else {
      scene.remove(explosionGroup);
      isAnimating = false;
    }
  }
  animateExplosion();
}

/**
 * Apply a respawn visual effect on a mesh
 */
export function applyPlayerRespawnVisual(mesh: THREE.Object3D): void {
  if (!mesh) return;

  const applyToMaterial = (material: THREE.Material) => {
    material.transparent = true;
    material.opacity = 0.5;
  };

  mesh.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      if (child.material instanceof THREE.Material) {
        applyToMaterial(child.material);
      } else if (Array.isArray(child.material)) {
        child.material.forEach(applyToMaterial);
      }
    }
  });
}
